/**
 * Every string the case detail renders.
 *
 * Two lines carry weight. The reply hint promises the notification the platform
 * actually sends — «case replied» is a configured event — and the state hint
 * says the opposite about itself: moving a case sends nothing, because a state
 * move with no words is a bookkeeping act and notifying on it would train
 * subscribers to ignore the channel.
 */
export const copy = {
  lede: (type: string, owner: string) =>
    `${type} من ${owner}. الخيط كامل أدناه، ومنه تردّ عليه وتضبط وضع الحالة.`,

  facts: {
    heading: 'تفاصيل الحالة',
    type: 'النوع',
    reason: 'السبب',
    reference: 'رقم الحالة',
    openedAt: 'فُتحت في',
    owner: 'صاحب الحالة',
    subject: 'الموضوع',
    /** Drawn on the two types that carry a subject; the block rider aims at it. */
    subjectOwner: 'مالك الموضوع',
    openSubjectOwner: 'افتح ملفّ المالك',
    unknown: 'غير مسجّل',
  },

  thread: {
    heading: 'الخيط',
    empty: 'لا رسالة في هذا الخيط بعد.',

    /**
     * The «awaiting user» notice takes the **info** kind here and the warning
     * kind on the subscriber host. The design system reserves warning for
     * guidance a reader can act on; on the subscriber's page the case is waiting
     * on them, and here it is waiting on someone else — so the same fact is a
     * neutral notice. The token follows what the sentence asks of its reader,
     * not what the state is called.
     */
    awaitingTitle: 'لا تنتظر عملاً منك الآن',
    awaitingBody: (owner: string) =>
      `آخر ما في الخيط منّا، والحالة موقوفة حتى يردّ ${owner}. تستطيع إضافة رسالة أخرى أو تغيير الوضع في أيّ وقت.`,

    /** The one state that refuses a reply, and the way forward from it. */
    closedTitle: 'هذه الحالة مغلقة',
    closedBody:
      'الخيط مقفل ولا يقبل رسالة جديدة. أعد وضعها إلى «مفتوحة» من الضابط أدناه إن كان فيها ما يُتابَع، أو اترك صاحبها يفتح حالة جديدة تقتبس رقم هذه.',
  },

  reply: {
    label: 'ردّك',
    placeholder: 'اكتب ردّك على صاحب الحالة…',
    hint: 'يظهر ردّك في هذا الخيط، ويصل صاحب الحالة إشعارٌ بأنّ حالته رُدّ عليها.',
    send: 'أرسل الردّ',
    clear: 'امسح',
    sent: 'أُرسل الردّ إلى صاحب الحالة.',
  },

  state: {
    heading: 'وضع الحالة',
    label: 'الوضع',
    apply: 'حدّث الوضع',
    hint: 'هذا هو الوضع الذي يراه صاحب الحالة في طابوره. تغييره لا يرسل إليه رسالة — أرسل ردّاً إن أردت أن تشرح له السبب.',
    /** The vocabulary read failed; the control has nothing honest to offer. */
    unavailable: 'تعذّر جلب أوضاع الحالات. أعد تحميل الصفحة قبل تغيير الوضع.',
    applied: (state: string) => `صار وضع الحالة «${state}».`,
  },

  /**
   * The decision, on a content report only.
   *
   * **The effect of each action is written in its own dialog, not above the
   * buttons.** A three-line paragraph over the action row is read before the
   * operator knows which decision they are taking, which is the wrong moment for
   * it — so the section carries one line and each mode's body carries its own
   * consequence. The deferred-audit-log fact sits in the delete dialog, which is
   * the one action it qualifies.
   */
  decision: {
    heading: 'القرار في الموضوع',
    note: 'يُطلَب تأكيدك قبل تنفيذ أيٍّ منها، ولا تُتراجَع من هذه الصفحة.',
    dismiss: 'اصرف النظر',
    unpublish: 'ألغِ نشر العنوان',
    remove: 'احذف العنوان',
    /** The line that explains why the three controls are disabled afterwards. */
    doneDismiss: 'صُرف النظر عن هذا البلاغ. وضع الحالة نفسه تضبطه من الضابط أدناه.',
    doneUnpublish: 'أُلغي نشر العنوان. لإعادة نشره افتحه في قائمة المحتوى.',
    doneRemove: 'حُذف العنوان وملفّاته. لا طريق للتراجع من أيّ سطح.',
    resolved: 'حُسم البلاغ.',
  },

  dialog: {
    /** One dialog, three modes: the shape is a question naming the title, an effect, and a verb. */
    dismiss: {
      heading: (subject: string) => `صرف النظر عن البلاغ على «${subject}»؟`,
      body: 'يُغلَق البلاغ دون تغيير على العنوان. يبقى منشوراً كما هو، ويصل القرار إلى المُبلِّغ في الخيط.',
      commit: 'اصرف النظر',
    },
    unpublish: {
      heading: (subject: string) => `إلغاء نشر «${subject}»؟`,
      body: 'يُرفع العنوان من كلّ أسطح القرّاء ويبقى في الفهرس بحالة «غير منشور». تعيده قائمة المحتوى إلى النشر متى شئت.',
      commit: 'ألغِ النشر',
    },
    remove: {
      heading: (subject: string) => `حذف «${subject}» نهائياً؟`,
      body: 'يُحذف العنوان ومواسمه وحلقاته وملفّاته المخزّنة. لا يمكن التراجع عن هذا الإجراء، ولا سجلّ يستعيده — التأكيد هنا هو الضمانة الوحيدة.',
      commit: 'احذف العنوان',
    },
    blockOwnerLabel: (owner: string) => `احجب المالك «${owner}» مع هذا القرار`,
    blockOwnerHint: 'يُمنع من الدخول إلى حسابه حتى يُرفع الحجب من صفحة حسابه.',
    replyLabel: 'ردّ يُرفق بالقرار',
    replyHint: 'اختياريّ. يصل إلى صاحب الحالة في الخيط مع القرار.',
  },

  states: {
    loading: 'جارٍ تحميل الحالة',
  },
} as const;
